/**
 * Permission Overrides API
 *
 * Functions for managing per-user permission grants and revokes.
 * Permission overrides allow admins to customize what individual users can do
 * beyond their base role permissions.
 */

import { pb } from '../lib/pocketbase'
import type { PermissionOverride } from '../types/agencyTeam'
import type { Action, ResourceType, Permission } from '../types/permissions'
import { ROLE_PERMISSIONS } from '../utils/permissions'
import type { ProfileRole } from '../types/agency'

/**
 * Get all permission overrides for a user
 */
export async function getPermissionOverrides(userId: string): Promise<PermissionOverride[]> {
  const user = await pb.collection('users').getOne<Record<string, any>>(userId)
  return user.permission_overrides ?? []
}

/**
 * Set all permission overrides for a user
 */
export async function setPermissionOverrides(
  userId: string,
  overrides: PermissionOverride[]
): Promise<PermissionOverride[]> {
  await pb.collection('users').update(userId, {
    permission_overrides: overrides,
  })
  return overrides
}

/**
 * Grant a specific permission to a user
 */
export async function grantPermission(
  userId: string,
  resource: ResourceType,
  action: Action
): Promise<PermissionOverride[]> {
  const currentOverrides = await getPermissionOverrides(userId)

  // Remove any existing override for this resource/action
  const filtered = currentOverrides.filter(
    o => !(o.resource === resource && o.action === action)
  )

  // Add new grant
  const newOverrides = [...filtered, { resource, action, granted: true }]

  return setPermissionOverrides(userId, newOverrides)
}

/**
 * Revoke a specific permission from a user
 */
export async function revokePermission(
  userId: string,
  resource: ResourceType,
  action: Action
): Promise<PermissionOverride[]> {
  const currentOverrides = await getPermissionOverrides(userId)

  // Remove any existing override for this resource/action
  const filtered = currentOverrides.filter(
    o => !(o.resource === resource && o.action === action)
  )

  // Add new revoke
  const newOverrides = [...filtered, { resource, action, granted: false }]

  return setPermissionOverrides(userId, newOverrides)
}

/**
 * Remove a permission override (restore to role default)
 */
export async function removeOverride(
  userId: string,
  resource: ResourceType,
  action: Action
): Promise<PermissionOverride[]> {
  const currentOverrides = await getPermissionOverrides(userId)

  // Remove the override
  const filtered = currentOverrides.filter(
    o => !(o.resource === resource && o.action === action)
  )

  return setPermissionOverrides(userId, filtered)
}

/**
 * Clear all permission overrides for a user
 */
export async function clearAllOverrides(userId: string): Promise<void> {
  await pb.collection('users').update(userId, {
    permission_overrides: [],
  })
}

/**
 * Get effective permissions for a user (role permissions + overrides)
 */
export function getEffectivePermissions(
  role: ProfileRole,
  overrides: PermissionOverride[] | null
): Permission[] {
  // Start with base role permissions
  const basePermissions = ROLE_PERMISSIONS[role] || []
  const effectivePermissions = [...basePermissions]

  if (!overrides || overrides.length === 0) {
    return effectivePermissions
  }

  for (const override of overrides) {
    if (override.granted) {
      // Grant: add permission if not already present
      const exists = effectivePermissions.some(
        p => p.resource === override.resource && p.action === override.action
      )
      if (!exists) {
        effectivePermissions.push({
          resource: override.resource,
          action: override.action,
        })
      }
    } else {
      // Revoke: remove permission if present
      const index = effectivePermissions.findIndex(
        p => p.resource === override.resource && p.action === override.action
      )
      if (index !== -1) {
        effectivePermissions.splice(index, 1)
      }
    }
  }

  return effectivePermissions
}

/**
 * Check if a user has a specific permission (considering overrides)
 */
export function hasPermissionWithOverrides(
  role: ProfileRole,
  overrides: PermissionOverride[] | null,
  resource: ResourceType,
  action: Action
): boolean {
  // First check overrides (they take precedence)
  if (overrides) {
    const override = overrides.find(
      o => o.resource === resource && o.action === action
    )
    if (override) {
      return override.granted
    }
  }

  // Fall back to role-based permissions
  const rolePermissions = ROLE_PERMISSIONS[role] || []

  // Check for 'manage' permission (grants all actions)
  const hasManage = rolePermissions.some(
    p => p.resource === resource && p.action === 'manage'
  )
  if (hasManage) return true

  // Check for specific action
  return rolePermissions.some(
    p => p.resource === resource && p.action === action
  )
}

/**
 * Get a summary of permission differences from role default
 */
export function getOverrideSummary(
  role: ProfileRole,
  overrides: PermissionOverride[] | null
): { granted: PermissionOverride[]; revoked: PermissionOverride[] } {
  const granted: PermissionOverride[] = []
  const revoked: PermissionOverride[] = []

  if (!overrides) {
    return { granted, revoked }
  }

  const rolePermissions = ROLE_PERMISSIONS[role] || []

  for (const override of overrides) {
    // Check if this is the default for the role
    const isRoleDefault = rolePermissions.some(
      p => p.resource === override.resource &&
        (p.action === override.action || p.action === 'manage')
    )

    if (override.granted && !isRoleDefault) {
      // Granted something not in role defaults
      granted.push(override)
    } else if (!override.granted && isRoleDefault) {
      // Revoked something that was in role defaults
      revoked.push(override)
    }
  }

  return { granted, revoked }
}

/**
 * Get all available permissions that can be configured
 */
export function getAllConfigurablePermissions(): Array<{
  resource: ResourceType
  action: Action
  description: string
}> {
  const resources: ResourceType[] = [
    'players', 'clients', 'prospects', 'contracts', 'transactions', 'invoices', 'deals',
    'messages', 'notes', 'calendar', 'teams', 'coaches',
    'compliance', 'tasks', 'settings', 'users'
  ]

  const actions: Action[] = ['create', 'read', 'update', 'delete', 'manage']

  const descriptions: Record<ResourceType, string> = {
    players: 'Players/Athletes',
    clients: 'Clients',
    prospects: 'Prospects',
    contracts: 'Contracts',
    transactions: 'Financial Transactions',
    invoices: 'Invoices',
    deals: 'Deals',
    messages: 'Messages',
    notes: 'Notes',
    calendar: 'Calendar Events',
    teams: 'Sports Teams',
    coaches: 'Coaches',
    compliance: 'Compliance Records',
    tasks: 'Tasks',
    settings: 'Settings',
    users: 'User Management',
  }

  const permissions: Array<{
    resource: ResourceType
    action: Action
    description: string
  }> = []

  for (const resource of resources) {
    for (const action of actions) {
      permissions.push({
        resource,
        action,
        description: `${action.charAt(0).toUpperCase() + action.slice(1)} ${descriptions[resource]}`,
      })
    }
  }

  return permissions
}
